import styled from 'styled-components';
import { CurrencyLevel } from 'economy';
import { ThumbnailScroller } from 'components/Panel/Chat/Conversation/styles';

interface BaseProps {
	isMobile?: boolean;
}

export const Base = styled.div<BaseProps>`
	width: 100%;
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0;
`;

interface PromptProps {
	isFocused: boolean;
}

export const Prompt = styled.div<PromptProps>`
	// layout
	display: flex;
	flex-direction: column;
	min-height: 0;

	// style
	background-color: ${({ theme }) => theme.gray[100]};
	border-radius: 10px;
	border: 1px solid ${({ theme }) => theme.gray[200]};
	transition: all 0.2s ease;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

	&:focus-within {
		border-color: ${({ theme }) => theme.gray[400]};
		box-shadow: 0 0 0 3px ${({ theme }) => `${theme.blue[300]}40`}, 0 2px 8px rgba(0, 0, 0, 0.06);
	}
`;

interface MultilineInputWrapperProps {
	isMobile?: boolean;
}

export const MultilineInputWrapper = styled.div<MultilineInputWrapperProps>`
	max-height: ${({ isMobile }) => (isMobile ? '150px' : '35vh')};
	overflow-y: auto;
	scrollbar-width: thin;
	scrollbar-color: ${({ theme }) => theme.gray[400]} ${({ theme }) => theme.gray[100]};

	&::-webkit-scrollbar {
		width: 6px;
	}

	&::-webkit-scrollbar-track {
		background: ${({ theme }) => theme.gray[100]};
	}

	&::-webkit-scrollbar-thumb {
		background-color: ${({ theme }) => theme.gray[400]};
		border-radius: 3px;
	}
	border-radius: 10px;
	text-align: left;
`;

interface ActionsProps {
	isMobile?: boolean;
}

export const Actions = styled.div<ActionsProps>`
	padding: 12px 16px;
	padding-right: ${({ isMobile }) => (isMobile ? '10px' : '16px')};
	display: flex;
	justify-content: flex-end;
	align-items: center;
	gap: 8px;
	border-top: 1px solid ${({ theme }) => theme.gray[200]};
`;

export const Thumbnails = styled(ThumbnailScroller)`
	// layout
	display: flex;
	margin-top: 8px;
	margin-left: 8px;
	margin-right: 8px;
`;

export const Attachments = styled(ThumbnailScroller)`
	// layout
	display: flex;
	margin-top: 8px;
	margin-left: 8px;
	margin-right: 8px;
`;

export const Spacer = styled.div`
	flex: 1;
`;

interface WrapperProps {
	isMobile?: boolean;
}

interface SubmitButtonWrapperProps extends WrapperProps {
	isDisabled?: boolean;
}

export const SubmitButtonWrapper = styled.button<SubmitButtonWrapperProps>`
	padding: 8px 16px;
	border-radius: 6px;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 6px;
	background: ${({ isDisabled, theme }) =>
		isDisabled
			? theme.gray[400]
			: `linear-gradient(135deg, ${theme.blue[500]} 0%, ${theme.blue[600]} 100%)`};
	box-shadow: ${({ isDisabled }) => (isDisabled ? 'none' : '0 2px 8px rgba(0, 142, 248, 0.3)')};
	transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
	cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'pointer')};
	opacity: ${({ isDisabled }) => (isDisabled ? 0.5 : 1)};
	border: none;
	outline: none;
	font-size: 13px;
	font-weight: 600;
	color: white;
	font-family: inherit;

	&:hover {
		background: ${({ isDisabled, theme }) =>
			isDisabled
				? theme.gray[400]
				: `linear-gradient(135deg, ${theme.blue[600]} 0%, ${theme.blue[700]} 100%)`};
		transform: ${({ isDisabled }) => (isDisabled ? 'none' : 'translateY(-1px)')};
		box-shadow: ${({ isDisabled }) => (isDisabled ? 'none' : '0 4px 12px rgba(0, 142, 248, 0.4)')};
	}

	&:active {
		transform: ${({ isDisabled }) => (isDisabled ? 'none' : 'translateY(-1px)')};
		box-shadow: ${({ isDisabled }) => (isDisabled ? 'none' : '0 3px 10px rgba(0, 142, 248, 0.3)')};
	}
`;

export const SubmitButtonIcon = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 16px;
	height: 16px;
	flex-shrink: 0;
`;

export const SubmitButtonText = styled.span`
	white-space: nowrap;
`;

export const LoadingWrapper = styled.div<WrapperProps>`
	padding-top: ${({ isMobile }) => (isMobile ? '6px' : '4px')};
`;

export const ExamplePromptLink = styled.button`
	background: none;
	border: none;
	color: ${({ theme }) => theme.gray[600]};
	text-decoration: underline;
	cursor: pointer;
	font-size: 14px;
	font-family: inherit;
	padding: 0;
	margin: 0;
	margin-top: 12px;
	text-align: center;
	align-self: center;

	&:hover {
		color: ${({ theme }) => theme.gray[700]};
	}
`;

interface GoldIndicatorWrapperProps {
	walletLevel?: CurrencyLevel;
}

export const GoldIndicatorWrapper = styled.div<GoldIndicatorWrapperProps>`
	position: relative;
	padding: 2px 3px;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: all 0.2s ease;
	border-radius: 5px;
`;

interface GoldCircleProps {
	walletLevel?: CurrencyLevel;
}

export const GoldCircle = styled.div<GoldCircleProps>`
	width: 14px;
	height: 14px;
	border-radius: 50%;
	border: 2.5px solid ${({ theme }) => theme.gray[200]};
	background-color: #ffeaa7;
	transition: all 0.2s ease;
	position: relative;
	opacity: ${({ walletLevel }) => (walletLevel === 'EMPTY' ? 0.4 : 1)};
`;

export const ErrorIconOverlay = styled.div`
	position: absolute;
	top: 0px;
	right: 0px;
	width: 14px;
	height: 14px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 1;
`;
